import {
  ScheduleType,
  USER_SUBTYPE,
  type AlarmContactId,
  type BasicCallhomeSite,
  type CallhomeAiTag,
  type CallhomeCamera,
  type CallhomeCameraGroup,
  type CallhomeSite,
  type CameraGroupId,
  type CameraLinkId,
  type CHCameraId,
  type CHServiceDisplayLayout,
  type CHStreamCloudRetentionId,
  type CHStreamId,
  type CreateCHServiceDisplayDayPartDto,
  type CustomerEntity,
  type DeviceEntity,
  type DeviceId,
  type EventFlow,
  type EventId,
  type EventState,
  type EventStatePayload,
  type GuestPayload,
  type LocationGroupEntity,
  type RsaRuleEntity,
  type ScheduleState,
  type ServiceDisplayDayPart,
  type UpdateCHServiceDisplayDayPartDto,
  type UserDefaults,
  type UserId,
  type UserRoleDefaults,
  type UserRoleEntity,
} from '@solinkcorp/api-types';

import {
  USER_TYPE_ADMIN,
  USER_TYPE_STANDARD,
  type AspenRolloutPolicy,
  type CallHomeUser,
} from '@solinkcorp/authentication';
import {
  type AlarmContactDto,
  type AlarmsVerifEyeConfigDto,
  type CloudId,
  type components,
  type CustomerId,
  type OperatingHoursPlaceCandidateDto,
  type OperatingHoursPlaceSummaryDto,
  type operations,
  type ResellerId,
  type ScheduleDto,
  type SiteId,
  type SyncOperatingHoursDto,
} from '@solinkcorp/callhome';
import {
  type EventStandardDefs,
  type FeatureStandardDefs,
  type UserFeatureStandardDefs,
} from '@solinkcorp/event-standard';
import { type ServiceDisplayWidget } from '@solinkcorp/service-display-monitor';
import { type WeeklySchedule } from '@solinkcorp/solink-schedule';
import { type CallHomeWidget } from '@solinkcorp/vd-common';
import {
  type AiNotification,
  type AiTag,
  type AlarmContactEntity,
  type AlarmContactSiteAssignment,
  type CallhomeAlarmConfig,
  type CallhomeAlarmState,
  type CallHomeReportTemplate,
  type Point,
  type ScheduledActivityEntity,
} from 'clients/CallhomeApiTypes';
import { type EsSearch } from 'components/DiscoverPage/types';
import { type RiskFlagRule } from 'components/EventsPage/RiskFlag/RiskFlagForm/riskFlagDefinition';
import { type ThumbnailLevel } from 'components/VideoPlayer/components/PlayerThumbnail/VideoThumbnail';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type { SetOptional, SetRequired } from 'type-fest';
import { ALARM_CONTACT_TYPES, IDENTITY_TYPE } from 'vd-constants';
import { type IEvent } from '../platform/platform';
import { type AbstractRequestor } from '../shared/AbstractRequestor';
import AppCache, { expiresIn } from '../shared/AppCache';
import { type IParams } from '../shared/IRequestor';
import {
  type CallHomePOS,
  type CloudStoragePackage,
  type ILocationCustomer,
  type StreamCloudRetention,
} from './types';

const constants = {
  REAL_TIME_NOTIFICATION_PUSH: 'REAL_TIME_NOTIFICATION_PUSH',
  REAL_TIME_NOTIFICATION_SMS: 'REAL_TIME_NOTIFICATION_SMS',
  REAL_TIME_NOTIFICATION_EMAIL: 'REAL_TIME_NOTIFICATION_EMAIL',
  IMPORT_TAGS_OPERATION_TYPE_ADD: 'ADD',
  IMPORT_TAGS_OPERATION_TYPE_REPLACE: 'REPLACE',
};

type PartialCallhomeSiteWithTags = Pick<CallhomeSite, 'id' | 'locationTags'>;

interface CallhomeFilter {
  skip?: number;
  limit?: number;
  order?: string;
  where?: any;
  fields?: string[];
  include?: any[];
}

type CallhomePaginationFilter = SetRequired<CallhomeFilter, 'skip' | 'limit' | 'order'>;

// This is the max limit set in the Callhome API, so doesn't make sense to increase it
const LIMIT_SITES = 100;

// TODO can split into parts if needed by other endpoints/conditions
const siteIncludeFilter = [
  // Below is an extension of the default Callhome cameras filter
  {
    relation: 'cameras',
    scope: {
      fields: [
        // Fields included by default Callhome filter
        'id',
        'cloudId',
        'resellerId',
        'customerId',
        'siteId',
        'deviceId',
        'cameraId',
        'overrideName',
        'name',
        'geolocation',
        'streams',
        'status',
        'deviceUrl',
        'deviceLocalUrl',

        // Fields used by VD and not in default Callhome filter
        'thumbnail',
        'ip',
        'isMotionEnabled',
        'manufacturer',
        'model',
        'firmwareVersion',
        'serialNumber',
        'identifiedBy',
        'isMotionAlarmEnabled',
        'ai',
        'alarms',
        'audioParams',
        'annotations',
        'motionParams',
        '360Settings',
        'segmentThumbnail',
        'cameraTagIds',
        'regions',
        'ptzCapabilities',
        'isPtzEnabled',

        // Fields defined by VD (CallhomeCamera) but not used and not in default Callhome filter
        // TODO IMO these should be removed to not unnecessary get more data (i.e. we're also trying to slim things down)
        'vmsName',
        'access',
        'hardwareId',
        'isUnplugged',
        'isOutOfFocus',
        'createdAt',

        // TODO used by VD and defined in Callhome, but wasn't this from Alarms v1?
        'motionAlarmScheduleId',
        'turnUrl',
      ],
      // TODO used by VD and defined in Callhome, but wasn't this from Alarms v1?
      include: ['motionAlarmSchedule'],
    },
  },
  {
    // This is also part of the default Callhome cameras filter
    relation: 'cameraLinks',
  },
  {
    relation: 'devices',
    scope: {
      fields: [
        // Fields included by default Callhome filter
        'id',
        'cloudId',
        'resellerId',
        'customerId',
        'siteId',
        'address',
        'name',
        'overrideName',
        'state',
        'nextCheckin',
        'lastCheckin',

        // Fields used by VD (CallhomeSite/DeviceEntity) and not in default Callhome filter
        'currentSoftwareMajorVersion',
        'currentSoftwareVersion',
        'ipAddress',
        'useLocalIpAsPrimary',
        'cloudOnly',
        'speedTestUploadRateS3',
        'latestSpeedTestResults',
        'isVirtual',
        'connectSettings',
        'deviceUrl',
        'deviceUrlV2',
        'deviceLocalUrl',
        'metadata',
        'features',
        'turnUrl',
        'availablePlaybackMethods',
        'tags',
        'status',
        'type',
        'tpuStatus',
        'timezoneId',

        // Fields defined by VD (CallhomeSite/DeviceEntity) but not used in VD and not in default Callhome filter
        // timezoneName,
        // timezoneId,
        // 'isPushToCloudEnabled',
        // 'activated',
        // 'noteId',
        // 'enableMonitoring',
        // 'deviceCapacity',
        // 'availableCapacity',
        // 'defaultHealthContacts',
        // 'location',
        // 'externalId',
        // 'checkinInterval',
        // 'username',
        // 'password',
        // 'vmsPort',
        // 'vmsHttpsPort',
        // 'motionAlarmScheduleId',
        // 'overrideIpAddress',
        // 'overrideLocation',
        // 'overrideAddress',
        // 'connectPort',
        // 'overrideConnectPort',
        // 'overrideVmsPort',
        // 'overrideVmsHttpsPort',
        // 'overrideLocalVmsPort',
        // 'overrideLocalVmsHttpsPort',
        // 'overrideCheckinPort',
        // 'listenerPort',
        // 'overrideListenerPort',
        // 'overrideConfigForwardPort',
        // 'deviceAdminPort',
        // 'overrideDeviceAdminPort',
        // 'sshPort',
        // 'overrideSshPort',
        // 'overrideExternalId',
        // 'localIP',
        // 'overrideLocalIP',
        // 'baseUrl',
        // 'overrideBaseUrl',
        // 'baseLocalUrl',
        // 'overrideLocalBaseUrl',
        // 'useBaseUrl',
        // 'expectedHttpsVmsPort',
        // 'expectedHttpVmsPort',
        // 'expectedSupervisorPort',
        // 'checkinPort',
        // 'expectedCheckinPort',
        // 'expectedListenerPort',
        // 'expectedCameraForwardingPort',
        // 'cameraForwardingPort',
        // 'isDeployed',
        // 'deploymentDate',
        // 'ignoreDeployUntil',
        // 'vmsBucket',
        // 'totalCameras',
        // 'onlineCameras',
        // 'percentageOnlineCameras',
        // 'offlineCameras',
        // 'softwareVersions',
        // 'ngrokConfig',
        // 'vmsConfig',
        // 'isCentrallyManaged',
        // 'deactivationDate',
        // 'assignedRelayUrl',
        // 'eventServerUrl',
        // 'latestDeviceCheckinId',
        // 'connectTasks',
        // 'eventQueueUrl',
        // 'eventQueueArn',
        // 'speedTestLastPolledVms',
        // 'speedTestLastPushTime',
        // 'speedTestUploadRateS3LatestPushes',
        // 'hostname',
        // 'metrics',
        // 'hardwareSerialNumber',
        // 'hardwareModelName',
        // 'hardwareProductName',
        // 'businessHours',
        // 'googlePlaceId',
        // 'contactInformation',
        // 'relayServerId',
        // 'latestConnectBackupId',
        // 'visionServerUrl',
        // 'totalMemory',
        // 'totalCameraBitrate',
        // 'notificationMetadata',
        // 'operatingHoursScheduleId',
        // 'integrations',
        // 'httpVmsPort',
        // 'httpsVmsPort',
        // 'vmsLocalPort',
        // 'vmsLocalHttpsPort',
        // 'vmsUrl',
        // 'vmsHttpsUrl',
        // 'vmsLocalUrl',
        // 'vmsLocalHttpsUrl',
        // 'vmsRelayUrl',
      ],
      // TODO operatingHoursSchedule is missing from Sites API, but should probably be there as its used by VD?
      // Note: Not adding relation data for cameras, posDevices, motionAlarmSchedule, alarmContacts, alarmConfig from devices as these should come from site
      include: ['operatingHoursSchedule'],
    },
  },
  {
    relation: 'posDevices',
    fields: [
      'id',
      'siteId',
      'deviceId',
      'name',

      // In Callhome default filter but not used by VD
      'vmsCameraIds',
      'externalId',
      'parserFamilyId',
      'cloudId',
      'resellerId',
      'customerId',
    ],
  },
  {
    // This is also part of the default Callhome cameras filter
    relation: 'alarmConfig',
  },
  {
    // This is also part of the default Callhome cameras filter
    relation: 'alarmContacts',
  },
  {
    // This is also part of the default Callhome cameras filter
    relation: 'motionAlarmSchedule',
  },
];

export interface PushNotificationBinding {
  /** Display label. NOT usable as a delete key. Use `token` instead. */
  id: string;
  /** The full FCM token. */
  token: string;
  platform: string;
}

export interface DerivedStream {
  level: ThumbnailLevel;
  streamCloudRetentionId: CHStreamCloudRetentionId | null;
}

export class CallhomeApi {
  constructor(private readonly requestor: AbstractRequestor) {}

  createAggregation({
    name,
    description,
    query,
    userId,
    locale = 'en-CA',
    siteIds,
  }: {
    name: string;
    description?: string;
    query: EsSearch;
    userId: string;
    locale?: string;
    siteIds: SiteId[];
  }) {
    return this.createReportTemplate({
      reportType: 'aggregation',
      name,
      description,
      query,
      userId,
      siteIds,
      locale,
    });
  }

  listAggregations({ userId }: { userId: string }): Promise<unknown[]> {
    return this.listReports({ userId, reportType: 'aggregation', onlyVisible: true });
  }

  createGuest({
    cameraIds,
    email,
    emailSubstitutions,
    eventTypes,
    expiresAt,
    features,
    locationIds,
    metadata,
    url,
    videoPlaybackEndsAt,
    videoPlaybackStartsAt,
  }: GuestPayload) {
    const data = {
      cameraIds,
      email,
      emailSubstitutions,
      eventTypes,
      expiresAt,
      features,
      locationIds,
      metadata,
      url,
      videoPlaybackEndsAt,
      videoPlaybackStartsAt,
    };
    return this.requestor.post({ url: '/users/guest', data });
  }

  addUser(user: Record<string, unknown>) {
    return this.requestor.post({ url: '/users', data: user });
  }

  deleteUser(userId: string) {
    return this.requestor.delete({ url: `users/${userId}` });
  }

  changePassword(password: string) {
    return this.requestor.patch({ url: 'users/current/password', data: { password } });
  }

  enableMfa({ userId }: { userId: string }) {
    return this.requestor.post({ url: `/users/${userId}/mfa` });
  }

  disableMfa({ userId }: { userId: string }) {
    return this.requestor.delete({ url: `/users/${userId}/mfa` });
  }

  getEventStandard(customerId: string): Promise<EventStandardDefs> {
    // pin major version: https://github.com/SolinkCorp/event-standard/issues/235#issuecomment-1231457416
    return this.requestor.get({ url: `/customers/${customerId}/eventStandard?version=v5` });
  }

  getFeatureStandard(customerId: string): Promise<FeatureStandardDefs> {
    return this.requestor.get({ url: `/customers/${customerId}/featureStandard?version=v5` });
  }

  getUserFeatureStandard(customerId: string): Promise<UserFeatureStandardDefs> {
    return this.requestor.get({ url: `/customers/${customerId}/userFeatureStandard?version=v5` });
  }

  getEventStandardVersion(customerId: string): Promise<{ version: string }> {
    return this.requestor.get({ url: `/customers/${customerId}/eventStandard/version?version=v5` });
  }

  getCustomerDefaultSdmLayout(customerId: string): Promise<{ defaultSdmLayoutId: string }> {
    return this.requestor.get({
      url: `/customers/${customerId}`,
      params: {
        filter: JSON.stringify({
          fields: ['defaultSdmLayoutId'],
        }),
      },
    });
  }

  updateCustomerDefaultSdmLayout(
    customerId: string,
    defaultSdmLayoutId: string | null,
  ): Promise<{ defaultSdmLayoutId: string | null }> {
    return this.requestor.patch({
      url: `/customers/${customerId}`,
      data: { defaultSdmLayoutId },
    });
  }

  getLocationGroups({
    customerId,
    limit,
    skip,
    order,
  }: {
    customerId: string;
    limit?: number;
    skip?: number;
    order?: string;
  }): Promise<LocationGroupEntity[]> {
    const where: any = {
      and: [
        { customerId },
        {
          userType: {
            inq: [USER_TYPE_ADMIN, USER_TYPE_STANDARD],
          },
        },
      ],
    };

    return this.requestor.get({
      url: `/locationGroups`,
      params: {
        filter: JSON.stringify({
          limit,
          skip,
          order: order || 'name ASC',
          where,
        }),
      },
    });
  }

  /**
   * Returns a list of widgets on the user's dashboard.
   */
  async getUserDashboard(userId: string): Promise<CallHomeWidget[]> {
    let widgets: CallHomeWidget[] = await this.requestor.get<CallHomeWidget[]>({
      url: `/users/${userId}/homepage`,
    });
    widgets = widgets.filter((widget: CallHomeWidget) => !!widget);
    widgets.forEach((widget: CallHomeWidget) => processWidget(widget));
    return widgets;
  }

  updateUserDashboard(userId: string, widgetIds: []): Promise<unknown> {
    return this.requestor.patch({ url: `/users/${userId}`, data: { widgetIds } });
  }

  /**
   * Returns a list of all widgets the user has access to (including some that may
   * not be on their dashboard).
   */
  async getUserWidgets(userId: string): Promise<CallHomeWidget[]> {
    const widgets: CallHomeWidget[] = await this.requestor.get<CallHomeWidget[]>({
      url: `/users/${userId}/widgets`,
    });
    widgets.forEach((widget: CallHomeWidget) => processWidget(widget));
    return widgets;
  }

  async getWidget(widgetId: string): Promise<CallHomeWidget> {
    const widget: CallHomeWidget = await this.requestor.get<CallHomeWidget>({
      url: `widgets/${widgetId}`,
    });
    processWidget(widget);
    return widget;
  }

  async addWidget({
    cloudId,
    resellerId,
    customerId,
    userId,
    widget,
  }: {
    cloudId: string;
    resellerId: string;
    customerId: string;
    userId: string;
    widget: Record<string, unknown>;
  }): Promise<CallHomeWidget> {
    const data: any = {
      cloudId,
      resellerId,
      customerId,
      userId,
      ...widget,
    };
    const newWidget: CallHomeWidget = await this.requestor.post<CallHomeWidget>({
      url: `/widgets`,
      data,
    });
    processWidget(newWidget);
    return newWidget;
  }

  async cloneDefaultWidget({
    userId,
    defaultWidget,
    changes,
  }: {
    userId: string;
    defaultWidget: CallHomeWidget;
    changes: Record<string, unknown>;
  }) {
    const rootWidgetId = defaultWidget.rootWidgetId || defaultWidget.id;
    const original = get(defaultWidget, 'widgetSettings');
    sanitizeChanges(original, changes);
    const newWidget: CallHomeWidget = await this.requestor.post<CallHomeWidget>({
      url: `/widgets`,
      data: { ...changes, userId, rootWidgetId },
    });
    processWidget(newWidget);
    return newWidget;
  }

  async deleteWidget(widgetId: string): Promise<CallHomeWidget> {
    const deletedWidget: CallHomeWidget = await this.requestor.delete<CallHomeWidget>({
      url: `/widgets/${widgetId}`,
    });
    processWidget(deletedWidget);
    return deletedWidget;
  }

  async updateWidget({
    widgetId,
    widget,
    changes,
  }: {
    widgetId: string;
    widget: CallHomeWidget;
    changes: Record<string, unknown>;
  }): Promise<CallHomeWidget> {
    const original =
      get(widget, 'mergedData.parent.widgetSettings') || get(widget, 'mergedData.widgetSettings');
    sanitizeChanges(original, changes);
    const updatedWidget: CallHomeWidget = await this.requestor.patch<CallHomeWidget>({
      url: `/widgets/${widgetId}`,
      data: changes,
    });
    processWidget(updatedWidget);
    return updatedWidget;
  }

  getCustomer(): Promise<ILocationCustomer> {
    // TODO mention this endpoint, but not directly affected by sites
    return this.requestor.get({ url: '/locations/customer' });
  }

  getCustomerById(customerId: string): Promise<CustomerEntity> {
    return this.requestor.get({ url: `/customers/${customerId}` });
  }

  getResellerById(resellerId: string): Promise<any> {
    return this.requestor.get({ url: `/resellers/${resellerId}` });
  }

  getCloudById(cloudId: string): Promise<any> {
    return this.requestor.get({ url: `/clouds/${cloudId}` });
  }

  getUserFeatures(userId: string): Promise<any> {
    return this.requestor.get({ url: `/users/${userId}/features` });
  }

  getCustomerUsers(
    customerId: string,
    limit?: number,
    skip?: number,
    withGuests?: boolean,
  ): Promise<CallHomeUser[]> {
    const where: any = {
      and: [
        { customerId },
        {
          userType: {
            inq: [USER_TYPE_ADMIN, USER_TYPE_STANDARD],
          },
        },
      ],
    };

    if (!withGuests) {
      where.and.push({ identityType: { neq: IDENTITY_TYPE.GUEST_USER } });
    }

    // exclude auto generated tablet API_KEY subtype users but include null types
    where.and.push({
      or: [{ subType: { neq: USER_SUBTYPE.API_KEY } }, { subType: null }],
    });

    return this.requestor.get({
      url: `/users`,
      params: {
        filter: JSON.stringify({
          limit,
          skip,
          order: 'email ASC',
          where,
        }),
      },
    });
  }

  getDigestSettings({ userId }: { userId: string }) {
    return this.requestor.get({ url: `/users/${userId}/settings/digest` });
  }

  updateDigestSettings({ userId, settings }: { userId: string; settings: unknown }) {
    return this.requestor.patch({ url: `/users/${userId}/settings/digest`, data: settings });
  }

  getCameraRestrictions(userRoleId: string) {
    return this.requestor.get({ url: `/userroles/${userRoleId}?filter={include: ['cameras']}` });
  }

  getUserPreferences() {
    return this.requestor.get({ url: '/users/current/preferences' });
  }

  getUserDefaults(userId: string): Promise<UserDefaults> {
    return this.requestor.get({ url: `/users/${userId}/defaults` });
  }

  getUserRoleDefaults(userRoleId: string): Promise<UserRoleDefaults> {
    return this.requestor.get({ url: `/userroles/${userRoleId}/defaults` });
  }

  createUserRole(data: {
    customerId: string;
    name: string;
    description?: string;
  }): Promise<UserRoleEntity> {
    return this.requestor.post({ url: `/userroles`, data });
  }

  updateUserRole({
    userRoleId,
    data,
  }: {
    userRoleId: string;
    data: Partial<UserRoleEntity>;
  }): Promise<UserRoleEntity> {
    return this.requestor.patch({ url: `/userroles/${userRoleId}`, data });
  }

  deleteUserRole(userRoleId: string) {
    return this.requestor.delete({ url: `/userroles/${userRoleId}` });
  }

  getAllCameraGroups(filter: Record<string, unknown>) {
    return this.requestor.get<CallhomeCameraGroup[]>({
      url: `/cameraGroups`,
      params: {
        filter: JSON.stringify({
          where: { ...filter },
        }),
      },
    });
  }

  getCameraGroup(id: CameraGroupId) {
    return this.requestor.get<CallhomeCameraGroup>({ url: `/cameraGroups/${id}` });
  }

  updateCameraGroup(id: CameraGroupId, data: Record<string, unknown>) {
    return this.requestor.patch<CallhomeCameraGroup>({ url: `/cameraGroups/${id}`, data });
  }

  createCameraGroup(group: unknown) {
    return this.requestor.post<CallhomeCameraGroup>({ url: '/cameraGroups', data: group });
  }

  deleteCameraGroup(id: CameraGroupId) {
    return this.requestor.delete({ url: `/cameraGroups/${id}` });
  }

  enableMotionAlarmsOnCamera(id: CHCameraId) {
    return this.requestor.patch<CallhomeCamera>({
      url: `cameras/${id}?filter={"include": ["motionAlarmSchedule"]}`,
      data: {
        isMotionAlarmEnabled: true,
      },
    });
  }

  async setMotionAlarmScheduleForCamera({
    cameraId,
    state,
  }: {
    cameraId: string;
    state: ScheduleState;
  }) {
    try {
      const response: any = await this.requestor.put({
        url: `/cameras/${cameraId}/schedules/MOTION_ALARM`,
        data: {
          state,
        },
      });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // TODO why clear cache for locations here?
      // Invalidate the cache for locations
      AppCache.invalidate({ request: `/locations` });
      return response;
    } catch (error) {
      console.error('Error setting motion alarm schedule for camera', error);
      throw error;
    }
  }

  async setMotionAlarmScheduleForLocation({
    locationId,
    muteUntil,
    schedule,
    state,
  }: {
    locationId: SiteId;
    muteUntil: number;
    schedule: WeeklySchedule;
    state: ScheduleState;
  }) {
    try {
      const response: any = await this.requestor.put({
        url: `/sites/${locationId}/schedules/MOTION_ALARM`,
        data: {
          schedule,
          state,
          muteUntil,
        },
      });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for locations
      AppCache.invalidate({ request: `/locations` });
      return response;
    } catch (error) {
      console.error('Error setting motion alarm schedule for location', error);
    }
  }

  async deleteMotionAlarmScheduleForLocation({ locationId }: { locationId: string }) {
    try {
      const response: any = await this.requestor.delete({
        url: `/sites/${locationId}/schedules/MOTION_ALARM`,
      });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for locations
      AppCache.invalidate({ request: `/locations` });
      return response;
    } catch (error) {
      console.error('Error deleting motion alarm schedule for location', error);
      throw error;
    }
  }

  listReports(
    {
      userId,
      reportType,
      onlyVisible,
    }: {
      userId: string;
      reportType: string;
      onlyVisible: boolean;
    },
    cacheRequest = true,
  ): Promise<CallHomeReportTemplate[]> {
    const params = [];
    if (onlyVisible) {
      params.push('onlyVisible=true');
    }

    if (reportType) {
      const filter = {
        where: {
          reportType,
        },
      };
      params.push(`filter=${JSON.stringify(filter)}`);
    }

    return this.requestor.get({
      url: `/users/${userId}/reporttemplates${params.length > 0 ? `?${params.join('&')}` : ''}`,
      expires: cacheRequest ? expiresIn(1).days() : 0,
    });
  }

  // Create report (assignment)
  async createReport({
    reportTemplateId,
    userId,
    isVisible,
    hasDigest,
    reportSettings,
  }: {
    reportTemplateId: string;
    userId: string;
    isVisible: boolean;
    hasDigest?: boolean;
    reportSettings?: Record<string, unknown>;
  }) {
    const data = generateReportData({ isVisible, hasDigest, reportSettings });

    try {
      const response: any = await this.requestor.post({
        url: `/v2/reports?filter={"include":"template"}`,
        data: { reportTemplateId, userId, ...data },
      });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for reports
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return response;
    } catch (error) {
      console.error('Error creating report', error);
      throw error;
    }
  }

  // Update report (assignment)
  async updateReport({
    userId,
    reportId,
    isVisible,
    hasDigest,
    reportSettings,
    inheritType,
  }: {
    userId: string;
    reportId: string;
    isVisible: boolean;
    hasDigest?: boolean;
    reportSettings?: Record<string, unknown>;
    inheritType?: string;
  }) {
    const data = generateReportData({ isVisible, hasDigest, reportSettings });

    if (inheritType) {
      data.inheritType = inheritType;
    }

    try {
      const response: any = await this.requestor.patch({ url: `/v2/reports/${reportId}`, data });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for reports
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return response;
    } catch (error) {
      console.error('Error updating report', error);
      throw error;
    }
  }

  // Update report template (definition)
  async updateReportTemplate({
    userId,
    templateId,
    data,
  }: {
    userId: string;
    templateId: string;
    data: Record<string, unknown>;
  }) {
    try {
      const response: any = await this.requestor.patch({
        url: `/reporttemplates/${templateId}`,
        data,
      });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for reports
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return response;
    } catch (error) {
      console.error('Error updating report template', error);
      throw error;
    }
  }

  // Create report template (definition)
  async createReportTemplate({
    name,
    description,
    reportType,
    query,
    userId,
    siteIds,
    locale = 'en-CA',
  }: {
    name: string;
    description?: string;
    reportType: string;
    query: EsSearch;
    userId: string;
    siteIds: SiteId[];
    locale?: string;
  }) {
    const data: any = {
      reportType,
      query,
      userId,
      siteIds,
      name: { [locale]: name },
      queryType: 'raw',
    };

    if (description) {
      data.description = { [locale]: description };
    }

    try {
      const response: any = await this.requestor.post({ url: '/reporttemplates', data });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for reports
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return response;
    } catch (error) {
      console.error('Error creating report template', error);
      throw error;
    }
  }

  // Delete report template (definition)
  async deleteReportTemplate({ templateId, userId }: { templateId: string; userId: string }) {
    try {
      const response: any = await this.requestor.delete({ url: `/reporttemplates/${templateId}` });
      if (response.error) {
        throw new Error(response.error.message);
      }
      // Invalidate the cache for reports
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return response;
    } catch (error) {
      console.error('Error deleting report template', error);
      throw error;
    }
  }

  async getRootReport({ templateId }: { templateId: unknown }) {
    if (!templateId) {
      // Otherwise all reports will be returned
      return Promise.reject(new Error('Missing report template ID'));
    }

    const filter = {
      where: {
        reportTemplateId: templateId,
        rootReportId: null,
      },
    };

    return this.requestor
      .get<any[]>({ url: `/v2/reports?filter=${JSON.stringify(filter)}` })
      .then((reports) => {
        if (!reports || reports.length !== 1) {
          // A single (root) report should have been created along with the newly created template
          throw new Error('Report create error: More than one or no root report returned');
        }

        const [rootReport] = reports;

        if (rootReport.reportTemplateId !== templateId) {
          throw new Error('Report create error: Report does not match template');
        }

        return rootReport;
      });
  }

  createUserNotificationForCreateReport({
    userId,
    reportTemplateId,
    hasEmail = false,
    hasPush = false,
    hasSms = false,
  }: {
    userId: string;
    reportTemplateId: string;
    hasEmail: boolean;
    hasPush: boolean;
    hasSms: boolean;
  }) {
    const notifications = {
      ...generateUserNotificationIsEnabledType({
        notificationType: constants.REAL_TIME_NOTIFICATION_PUSH,
        isEnabled: hasPush,
      }),
      ...generateUserNotificationIsEnabledType({
        notificationType: constants.REAL_TIME_NOTIFICATION_SMS,
        isEnabled: hasSms,
      }),
      ...generateUserNotificationIsEnabledType({
        notificationType: constants.REAL_TIME_NOTIFICATION_EMAIL,
        isEnabled: hasEmail,
      }),
    };

    return this.requestor.post({
      url: '/userNotifications',
      data: {
        userId,
        reportTemplateId,
        sourceType: 'REPORT',
        ...notifications,
      },
    });
  }

  createUserNotification({
    userId,
    reportTemplateId,
    notificationType,
    isEnabled,
  }: {
    userId: string;
    reportTemplateId: string;
    notificationType: string;
    isEnabled: boolean;
  }) {
    return this.requestor.post({
      url: '/userNotifications',
      data: {
        userId,
        reportTemplateId,
        sourceType: 'REPORT',
        ...generateUserNotificationIsEnabledType({ notificationType, isEnabled }),
      },
    });
  }

  updateUserNotification({
    userNotificationId,
    notificationType,
    isEnabled,
  }: {
    userNotificationId: string;
    notificationType: unknown;
    isEnabled: boolean;
  }) {
    return this.requestor.patch({
      url: `/userNotifications/${userNotificationId}`,
      data: generateUserNotificationIsEnabledType({ notificationType, isEnabled }),
    });
  }

  deleteUserNotification({ userNotificationId }: { userNotificationId: string }) {
    return this.requestor.delete({ url: `/userNotifications/${userNotificationId}` });
  }

  getRealTimeNotificationBindings({ userId }: { userId: string }) {
    return this.requestor.get({ url: `/users/${userId}/pushNotifications` });
  }

  createSmsBinding({ smsNumber }: { smsNumber: unknown }) {
    return this.createRealTimeNotificationBinding({ bindingType: 'sms', address: smsNumber });
  }

  createRealTimeNotificationBinding({
    bindingType,
    address,
  }: {
    bindingType: unknown;
    address: unknown;
  }) {
    return this.requestor.post({
      url: '/users/pushNotifications',
      data: {
        bindingType,
        address,
        confirm: true,
      },
    });
  }

  deleteRealTimeNotificationBinding({ userId, bindingId }: { userId: string; bindingId: string }) {
    return this.requestor.delete({ url: `/users/${userId}/pushNotifications/${bindingId}` });
  }

  getLocationCamerasWithSchedule({ siteId }: { siteId: SiteId }): Promise<CallhomeCamera[]> {
    // TODO motionAlarmSchedule on cmaeras was from alarms v1...?
    return this.requestor.get({
      url: `/cameras?filter={"where":{"siteId": "${siteId}"}, "include":["motionAlarmSchedule"]}`,
    });
  }

  async getLocationWithAlarmInfo({
    siteId,
  }: {
    siteId: SiteId;
  }): Promise<CallhomeSite | undefined> {
    return await this.requestor.get<CallhomeSite>({
      url: `/sites/${siteId}?filter={"include":["motionAlarmSchedule", "alarmConfig", "alarmContacts"]}`,
    });
  }

  async getAlarmConfigFromLocation({ siteId }: { siteId: SiteId }) {
    // GET /api/alarmConfigs/:id endpoint is not available to admin/standard users (TODO could bring this up)
    const site = await this.requestor.get<CallhomeSite>({
      url: `/sites/${siteId}?filter={"include":["alarmConfig"]}`,
    });
    return site.alarmConfig;
  }

  updateLocationAccess(siteId: SiteId, features: any) {
    return this.requestor.patch({
      url: `/sites/${siteId}`,
      data: {
        features,
      },
    });
  }

  /**
   * Update camera
   * @param {string} cameraId The camera ID (aka "Callhome camera ID", "Long camera ID", "Camera UUID")
   * @param {object} data The camera data to update
   */
  updateCamera(
    cameraId: CHCameraId,
    data: unknown,
    queryFilter: string = '',
    signal?: AbortSignal,
  ) {
    return this.requestor.patch<CallhomeCamera>({
      url: `/cameras/${cameraId}${queryFilter}`,
      data,
      signal,
    });
  }

  getUser({ userId }: { userId: UserId }): Promise<CallHomeUser> {
    return this.requestor.get({ url: `/users/${userId}` });
  }

  updateUser(userId: UserId, data: CallHomeUser) {
    return this.requestor.patch({ url: `/users/${userId}`, data });
  }

  pushNotifications(userId: UserId, fcmToken: string): Promise<PushNotificationBinding> {
    return this.requestor.post({
      url: `/users/${userId}/pushNotifications`,
      data: {
        bindingType: 'fcm',
        address: fcmToken,
        confirm: true,
      },
    });
  }

  async getLocations({ filter, timeout }: { filter?: CallhomeFilter; timeout?: number }) {
    const paginationFilter = (filter || {}) as CallhomePaginationFilter;

    // Add the include filter for the relation data
    // TODO should double-check code to see if include is needed everywhere getLocations is called (if not, need more logic to avoid unnecessary data querying)
    if (paginationFilter.include) {
      paginationFilter.include = paginationFilter.include.concat(siteIncludeFilter);
    } else {
      paginationFilter.include = siteIncludeFilter;
    }

    // Add the limit filter for pagination (note Callhome has a max limit, so exceeding this will cause incorrect results)
    if (!paginationFilter.limit || paginationFilter.limit > LIMIT_SITES) {
      paginationFilter.limit = LIMIT_SITES;
    }
    // Add the order filter for sorting needed by pagination
    paginationFilter.order = paginationFilter.order || 'name ASC';

    // Ensure only visible sites that are either active or virtual are returned
    const defaultWhereFilter = {
      and: [
        { visibilityStatus: 'VISIBLE' },
        {
          or: [{ status: 'ACTIVE' }, { status: 'VIRTUAL' }],
        },
      ],
    };

    paginationFilter.where = paginationFilter.where
      ? { and: [paginationFilter.where, defaultWhereFilter] }
      : defaultWhereFilter;

    const countWhere = paginationFilter.where ? { where: paginationFilter.where } : undefined;

    // Get the count of total sites (Callhome defaults to visible sites only) needed to determine the number of pages
    // (i.e. pagination requests). Any additions to the where clause of the filter should match to get the correct count.
    const countResponse: any = await this.requestor.request({
      method: 'GET',
      url: '/sites/count',
      options: { params: { filter: countWhere ? JSON.stringify(countWhere) : undefined } },
    });

    const totalPages = Math.ceil((countResponse.data?.count ?? 0) / paginationFilter.limit);

    // Use the last paginated request for the analytics
    let paginatedResponse: any;

    let locations = [] as CallhomeSite[];
    for (let i = 0; i < totalPages; i++) {
      try {
        // Add the number of records (sites) to skip for each subsequent pagination request
        paginationFilter.skip = i * paginationFilter.limit;
        paginatedResponse = await this._getPaginatedSites({ filter: paginationFilter, timeout });
        locations = [...locations, ...(paginatedResponse.data.results as CallhomeSite[])];
      } catch (error) {
        // If any single pagination request fails, the entire "Get Sites" operation should fail
        console.error('Error getting paginated sites', { page: i }, error);
        throw error;
      }
    }

    for (const location of locations || []) {
      for (const camera of location.cameras || []) {
        camera.geoLocation = (camera as any).location;
      }
    }

    return {
      results: locations,
      // If user doesn't have access to any sites, there will be no paginatedResponse
      statusCode: paginatedResponse?.status ?? countResponse?.status,
      total: locations.length,
      offset: filter?.skip || 0,
      count: locations.length,
      request: paginatedResponse?.request ?? countResponse?.request,
    };
  }

  private async _getPaginatedSites({
    filter = {
      skip: 0,
      limit: LIMIT_SITES,
      order: 'name ASC',
    },
    timeout,
  }: {
    filter: CallhomePaginationFilter;
    timeout?: number;
  }) {
    return this.requestor.request<any[]>({
      method: 'POST',
      url: '/sites/query',
      options: {
        timeout,
        data: {
          filter: JSON.stringify(filter),
          // return device features merged with customer-level, reseller-level and cloud-level ones
          // when it is true
          includeMergedFeatures: true,

          // Resolved VerifEye group per site.
          includeVerifEye: true,

          // Not using below that will result in default Callhome filter being used, instead supplying custom include filter
          // includeCameras: true,
          // includeDevices: true,
          // includePosDevices: true,
          // includeAlarms: true,
        },
      },
      isCacheable: true,
      expires: expiresIn(1).hours(),
    });
  }

  // TODO will need to test this - used for cloud status
  async isDeviceOffline(deviceId: string) {
    const filter = {
      fields: ['id', 'nextCheckin'],
    };

    const { nextCheckin } = await this.requestor.get<Partial<DeviceEntity>>({
      url: `/devices/${deviceId}`,
      params: { filter: JSON.stringify(filter) },
    });

    return !!nextCheckin && new Date(nextCheckin).getTime() < Date.now();
  }

  getPOSDevices(
    filter: { deviceId?: string; parserFamilyId?: string },
    options: { includeHierarchy: boolean } = { includeHierarchy: false },
  ): Promise<CallHomePOS[]> {
    const params: { where: any; include?: any } = { where: filter };
    if (options.includeHierarchy) {
      params.include = ['cloud', 'reseller', 'customer', 'device'];
    }
    return this.requestor.get<CallHomePOS[]>({
      url: `/posDevices?filter=${encodeURI(JSON.stringify(params))}`,
    });
  }

  patchPOSDevice(
    posDeviceId: string,
    data: { metadata?: { MOTION?: Record<string, unknown>; EVENT?: Record<string, unknown> } },
  ) {
    return this.requestor.patch<any[]>({
      url: `/posDevices/${posDeviceId}`,
      data,
    });
  }

  /** This can only be used for admin users due to the backend restrictions. */
  getAlarmConfig(siteId: SiteId) {
    return this.requestor.get<CallhomeAlarmConfig>({
      url: `/sites${siteId}/config/alarms`,
    });
  }

  /**
   * Alarm config merged with parent-level (cloud/reseller/customer) provider
   * settings, so provider-level config like `providerSettings.alarmSiteLabels`
   * is surfaced regardless of which level it's configured at.
   */
  getAlarmConfigWithParents(siteId: SiteId) {
    return this.requestor.get<CallhomeAlarmConfig>({
      url: `/sites/${siteId}/config/alarms?includeParents=true`,
    });
  }

  /** This can only be used for admin users due to the backend restrictions. */
  updateAlarmConfig(siteId: SiteId, data: Partial<CallhomeAlarmConfig>) {
    return this.requestor.patch<CallhomeAlarmConfig>({
      url: `/sites/${siteId}/config/alarms`,
      data,
    });
  }

  /** This can be used for both admin and standard users. */
  updateAlarmState(
    siteId: SiteId,
    data: SetOptional<CallhomeAlarmState, 'armUntil' | 'disarmUntil'>,
  ) {
    return this.requestor.patch<CallhomeAlarmConfig>({
      url: `/sites/${siteId}/alarms/state`,
      data,
    });
  }

  closeAlarm(siteId: SiteId, eventId: string) {
    return this.requestor.put<any[]>({
      url: `/sites/${siteId}/alarms/close?eventId=${eventId}`,
      data: {},
    });
  }

  async getSiteVerifEyeGroupId(customerId: CustomerId, siteId?: SiteId): Promise<string | null> {
    try {
      const url = siteId
        ? `/sites/${siteId}/config/verifEye?includeParents=true`
        : `/customer/${customerId}/config/verifEye`;

      const config = await this.requestor.get<AlarmsVerifEyeConfigDto>({ url });
      return config.alarmVerifEyeGroupId || null;
    } catch {
      return null;
    }
  }

  getAlarmContacts({
    customerId,
    limit,
    skip,
  }: {
    customerId: CustomerId;
    limit?: number;
    skip?: number;
  }) {
    return this.requestor.get<any[]>({
      url: `/alarmContacts/`,
      params: {
        filter: JSON.stringify({
          limit,
          skip,
          order: 'name ASC',
          where: {
            customerId,
            type: {
              inq: [ALARM_CONTACT_TYPES.STANDARD, ALARM_CONTACT_TYPES.EXTERNAL],
            },
          },
        }),
      },
    });
  }

  getAlarmGuestContacts({
    customerId,
    limit,
    skip,
  }: {
    customerId: string;
    limit?: number;
    skip?: number;
  }) {
    return this.requestor.get<any[]>({
      url: `/alarmContacts/`,
      params: {
        filter: JSON.stringify({
          limit,
          skip,
          order: 'name ASC',
          where: {
            customerId,
            type: ALARM_CONTACT_TYPES.GUEST,
          },
        }),
      },
    });
  }

  createNewAlarmContact(siteId: SiteId, data: Partial<AlarmContactEntity>) {
    return this.requestor.post<AlarmContactEntity>({
      url: `/sites/${siteId}/alarmContacts`,
      data,
    });
  }

  updateNewAlarmContact(
    siteId: SiteId,
    alarmContactId: AlarmContactId,
    data: Partial<AlarmContactEntity>,
  ) {
    return this.requestor.patch<AlarmContactEntity>({
      url: `/sites/${siteId}/alarmContacts/${alarmContactId}`,
      data,
    });
  }

  removeAlarmContact(siteId: SiteId, alarmContactId: AlarmContactId) {
    return this.requestor.delete<AlarmContactEntity>({
      url: `/sites/${siteId}/alarmContacts/${alarmContactId}`,
    });
  }

  resetAlarmContactAccessCode(
    siteId: SiteId,
    alarmContactId: AlarmContactId,
    data: { expiresAt?: number },
  ) {
    return this.requestor.post<any[]>({
      url: `/sites/${siteId}/alarmContacts/${alarmContactId}/accessCode`,
      data,
    });
  }

  // ─── Customer-level alarm contact endpoints (new schema) ──────────────────
  // Used when alarmConfig.providerSettings.alarmContactMigrated is true.
  // TODO SL-12707 Phase 3 cleanup: remove the site-level methods above once all
  // customers are migrated and only these customer-level methods remain.

  getCustomerAlarmContacts({ customerId }: { customerId: CustomerId }) {
    return this.requestor.get<AlarmContactDto[]>({
      url: `/customers/${customerId}/alarmContacts`,
    });
  }

  createCustomerAlarmContact({
    customerId,
    data,
  }: {
    customerId: CustomerId;
    data: Omit<Partial<AlarmContactDto>, 'id' | 'cloudId' | 'resellerId' | 'customerId'> & {
      siteAssignments: AlarmContactSiteAssignment[];
    };
  }) {
    return this.requestor.post<AlarmContactDto>({
      url: `/customers/${customerId}/alarmContacts`,
      data,
    });
  }

  updateCustomerAlarmContact({
    customerId,
    alarmContactId,
    data,
  }: {
    customerId: CustomerId;
    alarmContactId: string;
    data: Partial<AlarmContactDto> & { siteAssignments?: AlarmContactSiteAssignment[] };
  }) {
    return this.requestor.patch<AlarmContactDto>({
      url: `/customers/${customerId}/alarmContacts/${alarmContactId}`,
      data,
    });
  }

  deleteCustomerAlarmContact({
    customerId,
    alarmContactId,
  }: {
    customerId: CustomerId;
    alarmContactId: string;
  }) {
    return this.requestor.delete<void>({
      url: `/customers/${customerId}/alarmContacts/${alarmContactId}`,
    });
  }

  getCustomerAlarmContactAccessCode({
    customerId,
    alarmContactId,
  }: {
    customerId: CustomerId;
    alarmContactId: string;
  }) {
    return this.requestor.get<{ accessCode: string | null }>({
      url: `/customers/${customerId}/alarmContacts/${alarmContactId}/accessCode`,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────

  acceptEula() {
    return this.requestor.put<any[]>({
      url: `/users/eula`,
      data: {},
    });
  }

  createRecurringActivity(data: Partial<ScheduledActivityEntity>) {
    return this.requestor.post<ScheduledActivityEntity>({
      url: '/scheduledActivities',
      data,
    });
  }

  updateRecurringActivity(id: string, data: Partial<ScheduledActivityEntity>) {
    if (data.deviceId) {
      // deviceId is readonly, cannot be updated
      delete data.deviceId;
    }
    return this.requestor.patch<ScheduledActivityEntity>({
      url: `/scheduledActivities/${id}`,
      data,
    });
  }

  getRecurringActivity(id: string) {
    return this.requestor.get<ScheduledActivityEntity>({
      url: `/scheduledActivities?id=${id}`,
    });
  }

  listRecurringActivities(siteId?: string) {
    return this.requestor.get<ScheduledActivityEntity[]>({
      url: `/scheduledActivities`,
      params: siteId
        ? {
            filter: JSON.stringify({
              where: { siteId },
            }),
          }
        : undefined,
    });
  }

  getRecurringActivityById(id: string) {
    return this.requestor.get<ScheduledActivityEntity>({
      url: `/scheduledActivities/${id}`,
    });
  }

  deleteRecurringActivity(id: string) {
    return this.requestor.delete({
      url: `/scheduledActivities/${id}`,
    });
  }

  updateCameraLink(id: CameraLinkId, data: any) {
    return this.requestor.patch({
      url: `/cameralinks/${id}`,
      data,
    });
  }

  createCameraLink(data: CameraLink) {
    return this.requestor.post({
      url: `/cameralinks`,
      data,
    });
  }

  deleteCameraLink(id: CameraLinkId) {
    return this.requestor.delete({
      url: `/cameralinks/${id}`,
    });
  }

  /*
   * Location Tags - APIs
   * https://github.com/SolinkCorp/call-home-server/pull/2210
   */

  updateLocationTags({ id, locationTags }: PartialCallhomeSiteWithTags) {
    return this.requestor.patch({
      url: `/sites/${id}`,
      data: {
        locationTags,
      },
    });
  }

  updateMultipleLocationsTags(partialLocations: PartialCallhomeSiteWithTags[]) {
    return this.requestor.post({
      url: '/sites/locationTags/import',
      data: {
        operationType: constants.IMPORT_TAGS_OPERATION_TYPE_REPLACE,
        items: partialLocations,
      },
    });
  }

  updateTagsHierarchy(customerId: string, tagMetadata: any) {
    return this.requestor.patch({
      url: `/customers/${customerId}`,
      data: {
        tagMetadata,
      },
    });
  }

  updateAspenWebRollout(customerId: string, rollout: AspenRolloutPolicy) {
    return this.requestor.patch({
      url: `/customers/${customerId}/settings/aspenWeb`,
      data: { rollout },
    });
  }

  editAlarmEvent({
    eventId,
    siteId,
    payload,
  }: {
    eventId: EventId;
    siteId: SiteId;
    payload: Record<string, string>[] | Record<string, string>;
  }) {
    return this.requestor.put<IEvent>({
      url: `/sites/${siteId}/alarms/edit/${eventId}`,
      data: payload,
    });
  }

  getRiskFlagRules() {
    return this.requestor.get<RiskFlagRule[]>({
      url: '/rsaRules',
    });
  }

  getRiskFlagRuleById(id: string) {
    return this.requestor.get<RiskFlagRule>({
      url: `/rsaRules/${id}`,
    });
  }

  createRiskFlagRule(data: Omit<RsaRuleEntity, 'id'>) {
    return this.requestor.post<RsaRuleEntity>({
      url: '/rsaRules',
      data,
    });
  }

  updateRiskFlagRule(id: string, data: Partial<RiskFlagRule>) {
    return this.requestor.patch<RiskFlagRule>({
      url: `/rsaRules/${id}`,
      data,
    });
  }

  deleteRiskFlagRule(id: string) {
    return this.requestor.delete<RiskFlagRule>({
      url: `/rsaRules/${id}`,
    });
  }

  getEventFlows() {
    return this.requestor.get<EventFlow[]>({
      url: `/eventFlows`,
    });
  }

  createEventFlow(data: Omit<EventFlow, 'id'>) {
    return this.requestor.post<EventFlow>({
      url: `/eventFlows`,
      data,
    });
  }

  updateEventFlow(id: string, data: Partial<EventFlow>) {
    return this.requestor.patch<EventFlow>({
      url: `/eventFlows/${id}`,
      data,
    });
  }

  deleteEventFlow(id: string) {
    return this.requestor.delete<EventFlow>({
      url: `/eventFlows/${id}`,
    });
  }

  getEventState(eventId: string, workflowId: string, schemaId: string) {
    return this.requestor.get<EventState>({
      url: `/eventFlows/${workflowId}/events/${eventId}/schemas/${schemaId}`,
    });
  }

  getEventStateHistory(eventId: string, workflowId: string, schemaId: string) {
    return this.requestor.get<EventState[]>({
      url: `/eventFlows/${workflowId}/events/${eventId}/schemas/${schemaId}/history`,
    });
  }

  goToNextEventState(
    customerId: string,
    eventId: string,
    workflowId: string,
    schemaId: string,
    data: EventStatePayload,
  ) {
    return this.requestor.post<EventState>({
      url: `/eventFlows/${workflowId}/events/${eventId}/schemas/${schemaId}`,
      data: {
        customerId,
        ...data,
      },
    });
  }

  getDevices() {
    return this.requestor.get<DeviceEntity[]>({
      url: '/devices',
    });
  }

  resetEventFlowState(
    eventId: string,
    workflowId: string,
    schemaId: string,
    data: {
      customerId: string;
      // If empty, the workflow will be reopened.
      transition?: string;
      state?: string;
    },
  ) {
    return this.requestor.post<EventState>({
      url: `/eventFlows/${workflowId}/events/${eventId}/schemas/${schemaId}/reset`,
      data,
    });
  }

  getSites() {
    return this.requestor.get<CallhomeSite[]>({
      url: '/sites',
    });
  }

  async getCustomerCloudStoragePackage(customerId?: string) {
    if (!customerId) {
      return Promise.reject(new Error('Missing customer ID'));
    }

    try {
      const response = await this.requestor.get<{
        availableStorageSize: number;
        cloudStoragePackageId: string | null;
      }>({
        url: `/customers/${customerId}/cloudStorage`,
      });

      return response;
    } catch (error) {
      if (error instanceof Error) {
        // If the error is an instance of Error, we can safely access the message
        throw new Error(`Failed to fetch cloud storage package: ${error.message}`);
      } else {
        // For other types of errors, provide a generic fallback message
        throw new Error('Failed to fetch cloud storage package: Unknown error occurred');
      }
    }
  }

  bulkUpdateCameraStreams(
    data: {
      id: string;
      streamCloudRetentionId: string | null;
      derivedStreams: DerivedStream[];
    }[],
  ) {
    return this.requestor.patch<
      {
        id: CHStreamId;
        streamCloudRetentionId: CHStreamCloudRetentionId | null;
        derivedStreams: DerivedStream[];
      }[]
    >({
      url: '/cameraStreams/bulkUpdate',
      data: { data },
    });
  }

  getCallhomeCameraStreams() {
    return this.requestor.get<
      {
        id: CHStreamId;
        streamCloudRetentionId: CHStreamCloudRetentionId | null;
        derivedStreams: DerivedStream[];
      }[]
    >({
      url: '/cameraStreams',
      params: {
        filter: JSON.stringify({
          fields: ['streamCloudRetentionId', 'id', 'derivedStreams'],
        }),
      },
    });
  }

  getDeviceEntity(deviceId: string) {
    return this.requestor.get<DeviceEntity>({
      url: `/devices/${deviceId}`,
    });
  }
  patchDeviceEntity(deviceId: string, data: Partial<DeviceEntity>) {
    return this.requestor.patch<DeviceEntity>({
      url: `/devices/${deviceId}`,
      data,
    });
  }

  getCloudStoragePackages() {
    return this.requestor.get<CloudStoragePackage[]>({
      url: '/cloudStoragePackages',
    });
  }

  getStreamCloudRetentions() {
    return this.requestor.get<StreamCloudRetention[]>({
      url: '/streamCloudRetentions',
    });
  }

  getAiNotifications() {
    return this.requestor.get<AiNotification[]>({
      url: '/aiNotifications',
    });
  }

  getAiNotification(id: string) {
    return this.requestor.get<AiNotification>({
      url: `/aiNotifications/${id}`,
    });
  }

  getAiNotificationsCount() {
    return this.requestor.get<number>({
      url: '/aiNotifications/count',
    });
  }

  postAiNotification(data: AiNotification) {
    return this.requestor.post<AiNotification>({
      url: `/aiNotifications`,
      data,
    });
  }

  updateAiNotification(id: string, data: Partial<AiNotification>) {
    return this.requestor.patch<AiNotification>({
      url: `/aiNotifications/${id}`,
      data,
    });
  }

  deleteAiNotification(id: string) {
    return this.requestor.delete({
      url: `/aiNotifications/${id}`,
    });
  }

  getAiTag(filter: { where: { customerId: string } }) {
    return this.requestor.get<CallhomeAiTag[]>({
      url: '/aiTags',
      params: { filter: JSON.stringify(filter) },
    });
  }

  postAiTag(data: Omit<AiTag, 'id'>) {
    return this.requestor.post<AiTag>({
      url: '/aiTags',
      data,
    });
  }

  getSite<T = CallhomeSite>(siteId: SiteId, params?: IParams) {
    return this.requestor.get<T>({
      url: `/sites/${siteId}`,
      params,
    });
  }

  async querySites<T = CallhomeSite>(data: { filter: CallhomeFilter }) {
    const COUNT_LIMIT = 100;
    let totalCount: number | undefined;
    let allResults: T[] = [];
    try {
      const countResponse = await this.requestor.get<{ count: number }>({
        url: '/sites/count',
      });
      totalCount = countResponse.count;
    } catch (error) {
      throw new Error(`Failed to fetch sites count: ${error}`);
    }

    if (totalCount === 0) {
      return { results: allResults, count: 0 };
    }
    // Paginate the requests
    let skip = 0;
    while (skip < (totalCount ?? 0)) {
      try {
        const response = await this.requestor.request<{ results: T[]; count: number }>({
          url: `/sites/query`,
          method: 'POST',
          options: {
            timeout: 0,
            data: {
              filter: {
                ...data.filter,
                skip,
                limit: COUNT_LIMIT,
              },
            },
          },
        });

        allResults = allResults.concat(response.data.results);
        skip += COUNT_LIMIT; // Increment the skip value for the next batch
      } catch (error) {
        throw new Error(`Failed to fetch sites at offset ${skip}: ${error}`);
      }
    }

    return { results: allResults, count: totalCount || 0 };
  }

  /**
   * Fetches sites with device-only features (not merged with customer/reseller/cloud).
   * Use this when updating device features to avoid overwriting with inherited parent data.
   */
  async querySitesWithDeviceOnlyFeatures(
    siteIds: SiteId[],
  ): Promise<Array<CallhomeSite & { devices: Pick<DeviceEntity, 'id' | 'features'>[] }>> {
    if (siteIds.length === 0) {
      return [];
    }
    const COUNT_LIMIT = 100;
    type SiteWithDeviceFeatures = CallhomeSite & {
      devices: Pick<DeviceEntity, 'id' | 'features'>[];
    };
    let allResults: SiteWithDeviceFeatures[] = [];

    for (let skip = 0; skip < siteIds.length; skip += COUNT_LIMIT) {
      const batch = siteIds.slice(skip, skip + COUNT_LIMIT);
      try {
        const response = await this.requestor.request<{ results: SiteWithDeviceFeatures[] }>({
          url: '/sites/query',
          method: 'POST',
          options: {
            timeout: 0,
            data: {
              filter: {
                where: { id: { inq: batch } },
                include: [
                  {
                    relation: 'devices',
                    scope: { fields: ['id', 'features'] },
                  },
                ],
              },
              includeCameras: false,
              includeDevices: true,
              includePosDevices: false,
              includeAlarms: false,
              includeHidden: false,
              includeMergedFeatures: false,
            },
          },
        });
        allResults = allResults.concat(response.data.results ?? []);
      } catch (error) {
        throw new Error(`Failed to fetch sites with device features at offset ${skip}: ${error}`);
      }
    }

    return allResults;
  }

  getMinimizedSites() {
    return this.requestor.request<BasicCallhomeSite[]>({
      method: 'GET',
      url: '/sites/minimized',
      options: {},
    });
  }

  fetchCameras<T = CallhomeCamera>(filter: CallhomeFilter) {
    return this.requestor.get<T[]>({
      url: '/cameras',
      params: { filter: JSON.stringify(filter) },
    });
  }

  getAiTags(): Promise<AiTag[]> {
    return this.requestor.get<AiTag[]>({
      url: '/aiTags',
    });
  }

  createAiTag(data: Omit<AiTag, 'id'>): Promise<AiTag> {
    return this.requestor.post<AiTag>({
      url: '/aiTags',
      data,
    });
  }

  getServiceDisplayLayouts<T = CHServiceDisplayLayout>(): Promise<T[]> {
    return this.requestor.get<T[]>({
      url: '/serviceDisplayLayouts?filter={"include":[{"relation":"widgets","scope":{"include":["thresholds"]}}]}',
    });
  }

  updateServiceDisplayLayout<T extends CHServiceDisplayLayout = CHServiceDisplayLayout>(
    id: string,
    data: Partial<Pick<T, 'name'>>,
  ) {
    return this.requestor.patch<T>({
      url: `/serviceDisplayLayouts/${id}`,
      data,
    });
  }

  createServiceDisplayLayout<T extends CHServiceDisplayLayout = CHServiceDisplayLayout>(
    data: Pick<T, 'customerId' | 'name'>,
  ): Promise<T> {
    return this.requestor.post<T>({
      url: `/serviceDisplayLayouts`,
      data,
    });
  }

  deleteServiceDisplayLayout(id: string): Promise<void> {
    return this.requestor.delete({
      url: `/serviceDisplayLayouts/${id}`,
    });
  }

  updateServiceDisplayWidgets(
    layoutId: string,
    data: {
      widgets: ServiceDisplayWidget[];
    },
  ): Promise<{ widgets: ServiceDisplayWidget[] }> {
    return this.requestor.put<{ widgets: ServiceDisplayWidget[] }>({
      url: `/serviceDisplayLayouts/${layoutId}/widgets`,
      data,
    });
  }

  deleteServiceDisplayWidgets(
    layoutId: string,
    widgetIds: string[],
  ): Promise<ServiceDisplayWidget[]> {
    return this.requestor.delete<ServiceDisplayWidget[]>({
      url: `/serviceDisplayLayouts/${layoutId}/widgets`,
      data: { widgetIds },
    });
  }

  /** Passing an array of thresholdIds invokes a bulk delete operation. */
  bulkDeleteServiceDisplayDayPartThresholds(thresholdIds: string[]): Promise<unknown> {
    if (thresholdIds.length === 0) return Promise.resolve(undefined);
    return this.requestor.delete({
      url: '/serviceDisplayDayPartThresholds',
      data: { thresholdIds },
    });
  }

  getServiceDisplayDayParts<T = ServiceDisplayDayPart>(options?: {
    customerId?: string;
  }): Promise<T[]> {
    const filter =
      options?.customerId != null
        ? encodeURIComponent(JSON.stringify({ where: { customerId: options.customerId } }))
        : undefined;
    const url = filter ? `/serviceDisplayDayParts?filter=${filter}` : '/serviceDisplayDayParts';
    return this.requestor.get<T[]>({ url });
  }

  getServiceDisplayDayPart<T = ServiceDisplayDayPart>(id: string): Promise<T> {
    return this.requestor.get<T>({ url: `/serviceDisplayDayParts/${id}` });
  }

  createServiceDisplayDayPart(
    data: CreateCHServiceDisplayDayPartDto,
  ): Promise<ServiceDisplayDayPart> {
    return this.requestor.post<ServiceDisplayDayPart>({
      url: '/serviceDisplayDayParts',
      data,
    });
  }

  updateServiceDisplayDayPart(
    id: string,
    data: UpdateCHServiceDisplayDayPartDto,
  ): Promise<ServiceDisplayDayPart> {
    return this.requestor.patch<ServiceDisplayDayPart>({
      url: `/serviceDisplayDayParts/${id}`,
      data,
    });
  }

  deleteServiceDisplayDayPart(id: string): Promise<void> {
    return this.requestor.delete({ url: `/serviceDisplayDayParts/${id}` });
  }

  bulkUpdateDevicesFeatures(data: { id: DeviceId; features: Partial<DeviceEntity>['features'] }[]) {
    return this.requestor.patch<DeviceEntity[]>({
      url: '/devices/bulkUpdate',
      data: { data },
    });
  }

  async getOperatingHours(siteId: SiteId) {
    const schedules = await this.requestor.get<ScheduleDto[]>({
      url: `/sites/${siteId}/schedules`,
    });
    return schedules.find((s) => s.type === ScheduleType.OPERATING_HOURS);
  }

  async setOperatingHoursScheduleForLocation({
    locationId,
    schedule,
    state,
  }: {
    locationId: SiteId;
    schedule: WeeklySchedule;
    state: ScheduleState;
  }) {
    const response = await this.requestor.put<components['schemas']['ScheduleDto']>({
      url: `/sites/${locationId}/schedules/OPERATING_HOURS`,
      data: {
        schedule,
        state,
        muteUntil: 0,
      },
    });
    AppCache.invalidate({ request: `/locations` });
    return response;
  }
  syncOperatingHours(siteId: SiteId, options?: SyncOperatingHoursDto) {
    return this.requestor.post({
      url: `/sites/${siteId}/sync/operatingHours`,
      data: options,
    });
  }

  searchOperatingHoursPlaces(
    siteId: SiteId,
    {
      businessName,
      radius,
    }: NonNullable<
      operations['SitesSyncController_searchOperatingHoursPlaces']['parameters']['query']
    > = {},
  ): Promise<OperatingHoursPlaceCandidateDto[]> {
    const params = new URLSearchParams();
    if (businessName) params.set('businessName', businessName);
    if (radius) params.set('radius', String(radius));
    const query = params.toString();
    return this.requestor.get<OperatingHoursPlaceCandidateDto[]>({
      url: `/sites/${siteId}/sync/operatingHours/candidates${query ? `?${query}` : ''}`,
    });
  }

  async getOperatingHoursPlace(siteId: SiteId): Promise<OperatingHoursPlaceSummaryDto | null> {
    const summary = await this.requestor.get<OperatingHoursPlaceSummaryDto | null | ''>({
      url: `/sites/${siteId}/sync/operatingHours/place`,
    });
    // A null place serializes as an empty response body.
    return summary || null;
  }

  listApiKeys(): Promise<ApiKey[]> {
    return this.requestor.get<ApiKey[]>({
      url: '/apiKeys',
      params: { filter: JSON.stringify({ order: 'name ASC' }) },
    });
  }

  createApiKey(data: { name: string; customerId: CustomerId }): Promise<ApiKeyCredentials> {
    return this.requestor.post<ApiKeyCredentials>({ url: '/apiKeys', data });
  }

  // Rename only: the PATCH accepts just `name` and resolves to an `ApiKey`
  // (never `ApiKeyCredentials`), so no secret is exposed on rename — a contract
  // the param and return types enforce locally rather than relying on a comment.
  updateApiKey(id: ApiKeyConfigId, data: { name: string }): Promise<ApiKey> {
    return this.requestor.patch<ApiKey>({ url: `/apiKeys/${id}`, data });
  }

  deleteApiKey(id: ApiKeyConfigId): Promise<void> {
    return this.requestor.delete({ url: `/apiKeys/${id}` });
  }
}

/** CHS resource id for an ApiKeyConfig row — the PK used to delete the key. */
export type ApiKeyConfigId = string & { readonly __brand: 'ApiKeyConfigId' };

/** Underlying AWS API Gateway api-key id — distinct from {@link ApiKeyConfigId}. */
export type ApiKeyId = string & { readonly __brand: 'ApiKeyId' };

export interface ApiKey {
  id: ApiKeyConfigId;
  cloudId: CloudId;
  resellerId: ResellerId;
  customerId: CustomerId;
  dataResidencyRegionId: string;
  name: string;
  /**
   * OAuth client id — safe to display; returned by GET list/read (not the secret).
   * snake_case is intentional and mirrors the backend verbatim: call-home-server's
   * ApiKeyReadSchema adds `client_id` as an OAuth credential field name (paired with
   * `client_secret`, per RFC 6749), not a camelCase domain property like the sibling
   * fields. The value is resolved from the linked User.name on read.
   * See call-home-server: packages/management-api-types/src/schemas/models/api-key/api-key.schema.ts
   */
  client_id: string;
  apiKeyId: ApiKeyId;
  userId: UserId;
}

export interface ApiKeyCredentials {
  client_id: string;
  client_secret: string;
  'x-api-key': string;
}

interface CameraLink {
  deviceId: string;
  siteId?: string;
  toCameraId: string | null;
  fromCameraId: string;
  type: string;
  isEnabled: boolean;
  center: Point | undefined;
}

/**
 * Mutates widget for VD purposes by remapping the `mergedData.widgetSettings`
 * as the `widgetSettings` and the `mergedData.thresholds` as the `thresholds`.
 *
 * @param widget
 */
const processWidget = (widget: CallHomeWidget) => {
  if (widget.mergedData) {
    widget.widgetSettings = widget.mergedData.widgetSettings;
    widget.thresholds = widget.mergedData.thresholds;
  }
};

/**
 * Mutate `changes` by deleting all values except those that were actually changed
 * to be different than the original settings.
 *
 * @param original  Original data
 * @param changes   "Changes" to original data which might have unchanged data
 */
const sanitizeChanges = (original: any, changes: any) => {
  if (!isEmpty(original)) {
    return;
  }
  for (const key of Object.keys(original)) {
    if (isEqual(original[key], changes[key])) {
      delete changes[key];
    }
  }
};

const generateUserNotificationIsEnabledType = ({
  notificationType,
  isEnabled,
}: {
  notificationType: unknown;
  isEnabled: boolean;
}) => {
  const body: any = {};

  switch (notificationType) {
    case constants.REAL_TIME_NOTIFICATION_PUSH: {
      body.isPushNotificationsEnabled = isEnabled;
      break;
    }
    case constants.REAL_TIME_NOTIFICATION_SMS: {
      body.isSMSNotificationsEnabled = isEnabled;
      break;
    }
    case constants.REAL_TIME_NOTIFICATION_EMAIL: {
      body.isEmailNotificationsEnabled = isEnabled;
      break;
    }
    default: {
      throw new Error('User notification error: Unknown real-time notification type');
    }
  }

  return body;
};

const generateReportData = ({
  isVisible,
  hasDigest,
  reportSettings,
}: {
  isVisible?: boolean;
  hasDigest?: boolean;
  reportSettings?: Record<string, unknown>;
}) => {
  const data: any = {};
  if (isVisible !== undefined) {
    data.isVisible = isVisible;
  }
  if (hasDigest !== undefined) {
    data.hasDigest = hasDigest;
  }
  if (reportSettings !== undefined) {
    data.reportSettings = reportSettings;
  }
  return data;
};
